
import { featureFlags } from "@/utils/featureFlags";
import { PromptEvents } from './promptEvents';
import { UserJourneyEvents } from './userJourneyEvents';
import { AuthEvents } from './authEvents';

/**
 * Complete analytics service that combines all event tracking capabilities
 * by extending both prompt events and user journey events
 */
export class AnalyticsService extends PromptEvents {
  private userJourneyEvents: UserJourneyEvents;
  
  constructor() {
    super();
    
    // Create user journey events instance
    this.userJourneyEvents = new UserJourneyEvents();
  }
  
  /**
   * Track a page view
   * Override from core to ensure consistent tracking
   */
  trackPageView(path: string, title?: string) {
    super.trackPageView(path, title);
  }
  
  /**
   * Delegate user journey methods to the userJourneyEvents instance
   */
  trackCategorySelect(categoryId: string, categoryName: string) {
    this.userJourneyEvents.trackCategorySelect(categoryId, categoryName);
  }
  
  trackSearch(searchTerm: string, resultsCount: number) {
    this.userJourneyEvents.trackSearch(searchTerm, resultsCount);
  }
  
  trackScrollDepth(percentage: number, path?: string) {
    this.userJourneyEvents.trackScrollDepth(percentage, path);
  }
  
  trackReturnVisit(userId?: string, daysInactive?: number) {
    this.userJourneyEvents.trackReturnVisit(userId, daysInactive);
  }
  
  /**
   * Track authentication events with consistent user journey tracking
   */
  trackAuthEvent(eventType: 'sign_up' | 'sign_in' | 'sign_out', method: string, userId?: string) {
    this.userJourneyEvents.trackAuthEvent(eventType, method, userId || this.userId);
    
    // Also track through dedicated auth methods for more detailed tracking
    switch (eventType) {
      case 'sign_up':
        super.trackSignUp(method as any, true);
        break;
      case 'sign_in':
        super.trackSignIn(method as any, true);
        break;
      case 'sign_out':
        super.trackSignOut();
        break;
    }
  }
  
  /**
   * Override setUserId to update userId in both this instance and userJourneyEvents
   */
  setUserId(userId: string | null) {
    super.setUserId(userId);
    this.userJourneyEvents.setUserId(userId);
  }
}

// Create singleton instance
export const analytics = new AnalyticsService();
